import { type DocumentResult } from '@react-native-documents/picker';
import { Platform } from 'react-native';
import RNBlobUtil from 'react-native-blob-util';
import * as RNFS from 'react-native-fs';
import Share from 'react-native-share';

import { api } from './api';
import {
  isInlineAttachment,
  resolveAttachmentSource,
  type AttachmentSource,
} from './attachmentInlineFilter';
import { DownloadError } from './correspondence/getWithProgress';
import {
  copyFileToAndroidDownloads,
  saveToAndroidDownloads,
} from './utils/androidDownloadUtils';
import { normalizeFileUri } from './utils/fileUtils';

/**
 * The inline-vs-upload predicates live in `./attachmentInlineFilter` so that a
 * caller needing only those does not pull in this module's native file-system
 * dependencies (#1534). Re-exported here so every pre-existing import keeps
 * working unchanged.
 */
export {
  resolveAttachmentSource,
  isInlineAttachment,
  attachmentsForUploadStyleDisplay,
  filterInlineAttachmentsFromSectionMap,
  type AttachmentSource,
} from './attachmentInlineFilter';

export interface SavedAttachment {
  id: number;
  requestId: number;
  attachmentName: string;
  attachmentFile?: string; // Base64 data (only included in individual get)
  captions?: string;
  requestSection?: string;
  fileSizeBytes?: number;
  mimeType?: string;
  thumbnail?: string; // Base64 thumbnail data for videos
  thumbnailMimeType?: string; // MIME type of thumbnail (usually image/jpeg)
  attachmentSource?: AttachmentSource | null;
  /** List payloads sometimes still use the DB column name. */
  attachment_source?: AttachmentSource | null;
  createdAt?: string;
  updatedAt?: string;
}

/** True when an attachment may be embedded in the Summary PDF appendix photo grid (#1106 Issue 17). */
export function isPdfEmbeddableImageAttachment(
  attachment: Pick<SavedAttachment, 'mimeType' | 'attachmentSource'>,
): boolean {
  const mimeType = attachment.mimeType || '';
  if (mimeType.startsWith('image/')) {
    return true;
  }
  // Inline paste can arrive with a generic mime before normalization.
  return isInlineAttachment(attachment);
}

export interface AttachmentResponse {
  success: boolean;
  attachment?: SavedAttachment;
  attachments?: SavedAttachment[];
  error?: string;
}

// Request deduplication cache to prevent multiple simultaneous fetches
const pendingAttachmentRequests = new Map<
  number,
  Promise<AttachmentResponse>
>();

// Thumbnail cache to prevent re-fetching the same thumbnails
interface ThumbnailCacheEntry {
  thumbnailUrl: string;
  timestamp: number;
}
const thumbnailCache = new Map<number, ThumbnailCacheEntry>();
const THUMBNAIL_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Attachment result cache to prevent re-fetching full attachment data
interface AttachmentResultCacheEntry {
  result: AttachmentResponse;
  timestamp: number;
}
const attachmentResultCache = new Map<number, AttachmentResultCacheEntry>();
const ATTACHMENT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Pending thumbnail requests to prevent duplicate fetches
const pendingThumbnailRequests = new Map<number, Promise<string | null>>();

/**
 * Clear the thumbnail cache (useful for testing or when attachments are updated)
 */
export const clearThumbnailCache = (): void => {
  thumbnailCache.clear();
  console.log('🗑️ Thumbnail cache cleared');
};

/**
 * Clear the attachment result cache (useful for testing or when attachments are updated)
 */
export const clearAttachmentCache = (): void => {
  attachmentResultCache.clear();
  console.log('🗑️ Attachment result cache cleared');
};

/**
 * Clear all attachment-related caches
 */
export const clearAllAttachmentCaches = (): void => {
  thumbnailCache.clear();
  attachmentResultCache.clear();
  console.log('🗑️ All attachment caches cleared');
};

// Helper function to convert File to Base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove the data:mime/type;base64, prefix
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = error => reject(error);
  });
};

// Helper function to convert DocumentPicker file to Base64 (React Native)
const documentPickerFileToBase64 = async (
  file: DocumentResult,
): Promise<string> => {
  if (Platform.OS === 'web') {
    throw new Error('Use fileToBase64 for web platform');
  }

  try {
    // Ensure URI exists
    if (!file.uri) {
      throw new Error('File URI is missing');
    }

    // Get file size for validation
    const fileSize = file.size || 0;
    // Account for Base64 encoding overhead (~33% larger)
    // Set limit to 75MB for original file to ensure Base64 stays under 100MB
    const maxSizeBytes = 75 * 1024 * 1024; // 75MB limit for original file (will be ~100MB when Base64 encoded)

    if (fileSize > maxSizeBytes) {
      throw new Error(
        `File is too large (${Math.round(
          fileSize / 1024 / 1024,
        )}MB). Maximum size is 75MB.`,
      );
    }

    // Decode file:// paths (encoded spaces). Leave content:// document URIs
    // encoded — decoding them splits the document id and crashes Android.
    const normalizedUri = normalizeFileUri(file.uri);

    // Read file as base64
    // react-native-fs handles both file:// and content:// URIs
    const base64Data = await RNFS.readFile(normalizedUri, 'base64');

    if (!base64Data) {
      throw new Error('Failed to read file data');
    }

    console.log(
      `Successfully converted file to base64: ${file.name} (${Math.round(
        fileSize / 1024,
      )}KB)`,
    );

    return base64Data;
  } catch (error) {
    console.error('Error converting native file to base64:', error);

    // Provide helpful error messages
    if (error instanceof Error) {
      if (error.message.includes('permission')) {
        throw new Error(
          'Permission denied to read file. Please grant file access permissions.',
        );
      }
      if (
        error.message.includes('not found') ||
        error.message.includes('ENOENT')
      ) {
        throw new Error('File not found. It may have been deleted or moved.');
      }
    }

    throw new Error('Failed to process file. Please try again.');
  }
};

const EXTENSION_MIME_MAP: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/avi',
  wmv: 'video/wmv',
  webm: 'video/webm',
  mkv: 'video/mkv',
  m4v: 'video/m4v',
  msg: 'application/vnd.ms-outlook',
  eml: 'message/rfc822',
  txt: 'text/plain',
};

/**
 * Derive a MIME type from a filename's extension when the OS-provided type
 * is missing or generic (e.g. `application/octet-stream`, empty string).
 * Returns the original fallback when no mapping exists.
 */
function normalizeMimeTypeFromExtension(
  fileName: string,
  fallback: string = 'application/octet-stream',
): string {
  const extension = fileName.toLowerCase().split('.').pop();
  if (extension && EXTENSION_MIME_MAP[extension]) {
    console.log(
      `📄 Normalized MIME type from extension .${extension} to ${EXTENSION_MIME_MAP[extension]}`,
    );
    return EXTENSION_MIME_MAP[extension];
  }
  return fallback || 'application/octet-stream';
}

/**
 * Remap attachment request_section values after repeatable child ids persist (#1154 Bug 5).
 */
export const remapAttachmentRequestSections = async (
  requestId: number,
  remaps: Array<{ fromSection: string; toSection: string }>,
): Promise<{ success: boolean; remappedCount?: number; error?: string }> => {
  try {
    if (remaps.length === 0) {
      return { success: true, remappedCount: 0 };
    }

    const response = await api.post<{
      success: boolean;
      remappedCount: number;
    }>('/attachments/remap-sections', { requestId, remaps });
    clearAllAttachmentCaches();
    return {
      success: true,
      remappedCount: response.remappedCount,
    };
  } catch (error) {
    console.error('Failed to remap attachment sections:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Save a file as attachment to the database
 */
export const saveAttachment = async (
  file: File | DocumentResult,
  requestId: number,
  requestSection: string,
  attachmentSource: AttachmentSource,
  captions?: string,
  thumbnail?: string,
  thumbnailMimeType?: string,
  onProgress?: (progress: number) => void,
): Promise<AttachmentResponse> => {
  try {
    let base64Data: string;
    let fileName: string;
    let mimeType: string;
    let fileSize: number;

    if (Platform.OS === 'web' && file instanceof File) {
      // Web file processing
      base64Data = await fileToBase64(file);
      fileName = file.name;
      mimeType = file.type;
      fileSize = file.size;

      // Browsers return "" for unrecognised extensions (e.g. .msg, .eml).
      // Normalise using the same extension-based map used on native.
      if (!mimeType || mimeType === 'application/octet-stream') {
        mimeType = normalizeMimeTypeFromExtension(fileName, mimeType);
      }
    } else if (Platform.OS !== 'web' && 'uri' in file) {
      // Native file processing
      base64Data = await documentPickerFileToBase64(file);
      fileName = file.name || 'unknown';
      mimeType = file.type || 'application/octet-stream';
      fileSize = file.size || 0;

      // iOS sometimes returns empty or non-standard MIME types
      // Normalize based on file extension if MIME type is missing or generic
      if (
        !mimeType ||
        mimeType === 'application/octet-stream' ||
        mimeType === 'public.data'
      ) {
        mimeType = normalizeMimeTypeFromExtension(fileName, mimeType);
      }
    } else {
      throw new Error('Invalid file object');
    }

    console.log('🔗 DEBUGGING: attachmentService.saveAttachment called with:', {
      requestId,
      requestIdType: typeof requestId,
      fileName,
      mimeType,
      fileSize,
      requestSection,
      hasThumbnail: !!thumbnail,
      platform: Platform.OS,
    });

    // Log the raw file object to see what iOS is giving us
    if (Platform.OS !== 'web' && 'uri' in file) {
      console.log('📄 iOS File Details:', {
        uri: file.uri,
        type: file.type,
        name: file.name,
        size: file.size,
      });
    }

    const result = await api.post(
      '/attachments',
      {
        requestId,
        attachmentFile: base64Data,
        attachmentName: fileName,
        captions,
        requestSection,
        fileSizeBytes: fileSize,
        mimeType,
        thumbnail,
        thumbnailMimeType,
        attachmentSource,
      },
      {
        timeout: 300000, // 5 minute timeout for large file uploads
        onUploadProgress: onProgress,
        fileSize: fileSize, // For native progress estimation (Base64 encoded size)
      },
    );

    return result;
  } catch (error) {
    console.error('Attachment save error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Save failed',
    };
  }
};

/**
 * Get all attachments for a request
 */
export const getAttachmentsByRequest = async (
  requestId: number,
): Promise<AttachmentResponse> => {
  try {
    const result = await api.get<AttachmentResponse>(
      `/attachments/request/${requestId}`,
    );
    return result;
  } catch (error) {
    console.error('Get attachments error:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to get attachments',
    };
  }
};

/**
 * Get attachment metadata only (no Base64 file data) - lightweight for thumbnails
 * This is much faster than getAttachment() as it doesn't transfer the full file
 */
export const getAttachmentMetadata = async (
  attachmentId: number,
): Promise<AttachmentResponse> => {
  try {
    console.log(
      `📋 Fetching metadata for attachment ${attachmentId} (lightweight, no file data)`,
    );
    const result = await api.get<AttachmentResponse>(
      `/attachments/${attachmentId}/metadata`,
      {
        timeout: 10000, // 10 seconds is more than enough for metadata
      },
    );
    console.log(
      `✅ Successfully fetched metadata for attachment ${attachmentId}`,
    );
    return result;
  } catch (error) {
    console.error(
      `❌ Get attachment metadata error for ${attachmentId}:`,
      error,
    );
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to get attachment metadata',
    };
  }
};

/**
 * Get thumbnail for an image attachment with caching
 * Returns a data URL for the thumbnail or null if not available
 * Uses metadata endpoint instead of full file download for better performance
 */
export const getAttachmentThumbnail = async (
  attachmentId: number,
): Promise<string | null> => {
  // Check cache first
  const cached = thumbnailCache.get(attachmentId);
  if (cached && Date.now() - cached.timestamp < THUMBNAIL_CACHE_DURATION) {
    console.log(`💾 Using cached thumbnail for attachment ${attachmentId}`);
    return cached.thumbnailUrl;
  }

  // Check if there's already a pending request for this thumbnail
  const existingRequest = pendingThumbnailRequests.get(attachmentId);
  if (existingRequest) {
    console.log(
      `⏳ Reusing existing thumbnail request for attachment ${attachmentId}`,
    );
    return existingRequest;
  }

  // Create new request
  const requestPromise = (async () => {
    try {
      // Use metadata endpoint - much lighter than full file
      const result = await getAttachmentMetadata(attachmentId);

      if (!result.success || !result.attachment) {
        return null;
      }

      const { attachment } = result;

      // For images, check if we have a stored thumbnail, otherwise we need the full file
      if (attachment.mimeType?.startsWith('image/')) {
        // If we have a pre-generated thumbnail, use it
        if (attachment.thumbnail && attachment.thumbnailMimeType) {
          const thumbnailUrl = getViewableFileUrl(
            attachment.thumbnail,
            attachment.thumbnailMimeType,
          );

          // Cache the result
          thumbnailCache.set(attachmentId, {
            thumbnailUrl,
            timestamp: Date.now(),
          });

          return thumbnailUrl;
        }

        // No thumbnail available - we'd need to fetch the full file
        // For now, return null and let the component decide whether to load the full file
        console.log(
          `⚠️ No thumbnail available for image attachment ${attachmentId}`,
        );
        return null;
      }

      // For videos, check if we have a thumbnail
      if (attachment.mimeType?.startsWith('video/')) {
        if (attachment.thumbnail && attachment.thumbnailMimeType) {
          const thumbnailUrl = getViewableFileUrl(
            attachment.thumbnail,
            attachment.thumbnailMimeType,
          );

          // Cache the result
          thumbnailCache.set(attachmentId, {
            thumbnailUrl,
            timestamp: Date.now(),
          });

          return thumbnailUrl;
        }
      }

      return null;
    } catch (error) {
      console.error(
        `❌ Error loading thumbnail for attachment ${attachmentId}:`,
        error,
      );
      return null;
    } finally {
      // Clean up the pending request
      pendingThumbnailRequests.delete(attachmentId);
    }
  })();

  // Store the pending request
  pendingThumbnailRequests.set(attachmentId, requestPromise);

  return requestPromise;
};

/**
 * Get a specific attachment (with Base64 data for viewing)
 * Includes result caching and request deduplication to prevent multiple fetches
 */
export const getAttachment = async (
  attachmentId: number,
): Promise<AttachmentResponse> => {
  if (
    typeof attachmentId !== 'number' ||
    !Number.isInteger(attachmentId) ||
    attachmentId <= 0
  ) {
    return {
      success: false,
      error: 'Invalid attachment ID',
    };
  }

  // Check cache first
  const cached = attachmentResultCache.get(attachmentId);
  if (cached && Date.now() - cached.timestamp < ATTACHMENT_CACHE_DURATION) {
    console.log(`📦 Returning cached attachment ${attachmentId}`);
    return cached.result;
  }

  // Check if there's already a pending request for this attachment
  const existingRequest = pendingAttachmentRequests.get(attachmentId);
  if (existingRequest) {
    console.log(`⏳ Reusing existing request for attachment ${attachmentId}`);
    return existingRequest;
  }

  // Create new request
  const requestPromise = (async () => {
    try {
      console.log(
        `📥 Fetching attachment ${attachmentId} (large file, 10-minute timeout)`,
      );
      // Use 10-minute timeout for large file downloads (same as uploads)
      // Large files (70MB+) with Base64 encoding can take several minutes to transfer
      const result = await api.get<AttachmentResponse>(
        `/attachments/${attachmentId}`,
        {
          timeout: 600000, // 10 minutes for large file downloads
        },
      );
      console.log(`✅ Successfully fetched attachment ${attachmentId}`);

      // Cache successful results
      if (result.success) {
        attachmentResultCache.set(attachmentId, {
          result,
          timestamp: Date.now(),
        });
      }

      return result;
    } catch (error) {
      console.error(`❌ Get attachment error for ${attachmentId}:`, error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to get attachment',
      };
    } finally {
      // Clean up the pending request after completion (success or failure)
      pendingAttachmentRequests.delete(attachmentId);
    }
  })();

  // Store the pending request
  pendingAttachmentRequests.set(attachmentId, requestPromise);

  return requestPromise;
};

/**
 * Update attachment captions
 */
export const updateAttachment = async (
  attachmentId: number,
  captions: string,
): Promise<AttachmentResponse> => {
  try {
    const result = await api.put<AttachmentResponse>(
      `/attachments/${attachmentId}`,
      { captions },
    );
    // Invalidate cache on successful update
    if (result.success) {
      attachmentResultCache.delete(attachmentId);
      thumbnailCache.delete(attachmentId);
    }
    return result;
  } catch (error) {
    console.error('Update attachment error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Update failed',
    };
  }
};

/**
 * Delete an attachment
 */
export const deleteAttachment = async (
  attachmentId: number,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const result = await api.delete<{ success: boolean; error?: string }>(
      `/attachments/${attachmentId}`,
    );
    // Invalidate cache on successful delete
    if (result.success) {
      attachmentResultCache.delete(attachmentId);
      thumbnailCache.delete(attachmentId);
    }
    return result;
  } catch (error) {
    console.error('Delete attachment error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Delete failed',
    };
  }
};

/**
 * Convert Base64 to viewable data URL
 */
export const getViewableFileUrl = (
  base64Data: string,
  mimeType: string,
): string => {
  return `data:${mimeType};base64,${base64Data}`;
};

/**
 * Save these bytes to the device, whatever produced them.
 *
 * ⚠️ EXTRACTED FROM `downloadAttachment`, NOT WRITTEN AGAIN (#1069 Story 4.2,
 * 2026-09-14). Correspondence message files are not rows in `attachments` and
 * have no `attachmentId`, but saving bytes is identical on every platform:
 * a blob and an `<a download>` on web, the Downloads directory on Android, a
 * temp file and the share sheet on iOS. Copying those three branches for a
 * second caller is how one of them quietly stops matching the other.
 *
 * Behaviour is unchanged for the existing caller: `downloadAttachment` now
 * fetches the attachment and hands its bytes here.
 */
export const saveBase64File = async (
  base64Data: string,
  fileName: string,
  mimeType: string,
): Promise<void> => {
  if (Platform.OS === 'web') {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    saveBlobFile(blob, fileName);
    return;
  }
  await downloadAttachmentNative(base64Data, fileName, mimeType);
};

/**
 * Hand a Blob to the browser's own download UI (#1069 Story 7.5, D11, C9).
 *
 * Extracted from `saveBase64File`'s web branch, which now builds its Blob and
 * calls this. The zip transport (`getBlobWithProgress`) hands its response
 * Blob here DIRECTLY: a 200 MB zip must never pass through the one-number-
 * per-byte decode above, which is a dead tab at that size.
 *
 * Web only, by construction: a Blob is a web thing. Native streams to disk
 * (`downloadToDevice`).
 */
/** How long the object URL outlives the click; see `saveBlobFile`. */
export const REVOKE_OBJECT_URL_DELAY_MS = 1000;

export const saveBlobFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // ⚠️ THE REVOKE IS DEFERRED, NOT SYNCHRONOUS (review patch P6). `click()`
  // only STARTS the download; Firefox and Safari read the object URL after
  // it returns, and revoking it in the same turn can abort a 200 MB zip
  // mid-save. One second is well past the point where the browser holds
  // the Blob itself.
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_OBJECT_URL_DELAY_MS);
};

/**
 * Download an attachment to user's file system
 */
export const downloadAttachment = async (
  attachmentId: number,
): Promise<void> => {
  try {
    const result = await getAttachment(attachmentId);
    if (!result.success || !result.attachment?.attachmentFile) {
      throw new Error('Failed to get attachment data');
    }

    const { attachmentFile, attachmentName } = result.attachment;
    const mimeType = result.attachment.mimeType || 'application/octet-stream';

    await saveBase64File(
      attachmentFile,
      attachmentName || `attachment_${attachmentId}`,
      mimeType,
    );
  } catch (error) {
    console.error('Download attachment error:', error);
    throw error;
  }
};

/**
 * A file name that is safe to put in a path, on any platform.
 *
 * ⚠️ THE NAME IS UNTRUSTED TEXT AND OLD ROWS PREDATE THE WRITE-SIDE CHECK
 * (#1069 code review, 2026-09-14). Correspondence added `CONTROL_OR_SEPARATOR`
 * at its POST on 2026-09-14, which stops separators going in from now on; it
 * does nothing for rows written before it, which is exactly why the serving
 * route re-sanitises the same name into `Content-Disposition`. The header was
 * defended and this path was not: a name carrying `/` or `..` reached
 * `RNFS.writeFile` and `saveToAndroidDownloads` verbatim, so it either wrote
 * outside the directory intended or threw into the retry dialog as an
 * unexplained failure. Request attachments reach here too, and their names come
 * from the same kind of place.
 *
 * Deliberately conservative: separators, traversal, control characters and the
 * Windows-reserved set, collapsed to `_`. Not an allowlist, because a name is
 * how a person recognises their own file and stripping it to ASCII would hide
 * that far more often than it would protect anything.
 *
 * Exported since #1069 Story 7.5 (additive) so the client-side parity test
 * (`src/services/correspondence/__tests__/zipFileName.test.ts`) can hold the
 * server's copy in `server/services/correspondenceZipName.ts` byte-identical
 * to this one: the zip's entry names and a file saved on its own must agree.
 */
export const safeFileName = (fileName: string): string => {
  const flattened = fileName
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '_')
    .replace(/[/\\:*?"<>|]/g, '_')
    // Trim BEFORE the leading-dot rule: after it, `' ..'` kept its dots
    // (the space shielded them) and the trim then exposed `..`, a directory
    // name (#1069 MR !597 review). Both copies change together (C6).
    .trim()
    .replace(/^\.+/, '_');
  return flattened === '' ? 'file' : flattened;
};

/**
 * Hand a file that is ALREADY ON DISK to the platform's own "save" (#1069
 * Story 7.5, D11, AC 20). One Android hand-off and one iOS hand-off, for
 * bytes that came as base64 (`downloadAttachmentNative`) AND for bytes that
 * were streamed straight to the path and never entered JavaScript
 * (`downloadToDevice`). Two copies of this would drift the way the two
 * `saveBase64File` callers once did.
 *
 * Android: `copyFileToAndroidDownloads` (MediaStore on API 29+, a move into
 *   Downloads below), which owns the temp file's cleanup.
 * iOS: the share sheet on `file://path`, so the user can choose "Save to
 *   Files"; the temp file is unlinked after a short delay so the share can
 *   finish reading it.
 *
 * `fileName` is expected to be sanitised already (`safeFileName`).
 */
export const handOffNativeFile = async (
  path: string,
  fileName: string,
  mimeType: string,
): Promise<void> => {
  if (Platform.OS === 'android') {
    await copyFileToAndroidDownloads(path, fileName, mimeType);
    return;
  }

  if (Platform.OS === 'ios') {
    try {
      await Share.open({
        title: 'Save File',
        url: `file://${path}`,
        type: mimeType,
        filename: fileName,
        failOnCancel: false,
      });
    } catch (shareError: unknown) {
      // User dismissed the share sheet — not a real error.
      const msg: string =
        shareError instanceof Error ? shareError.message : String(shareError);
      if (msg.includes('CANCELLED') || msg.includes('User did not share')) {
        return;
      }
      throw shareError;
    } finally {
      // Clean up temp file after a short delay to let the share complete.
      setTimeout(async () => {
        try {
          if (await RNFS.exists(path)) {
            await RNFS.unlink(path);
          }
        } catch (e: unknown) {
          // Best-effort cleanup — do not re-throw so the share result is unaffected.
          console.warn('Failed to clean up temp file after share:', e);
        }
      }, 5000);
    }
    return;
  }

  throw new Error('Unsupported platform for native download');
};

/**
 * Download attachment on native platforms.
 *
 * Android: delegates to `saveToAndroidDownloads` (MediaStore on API 29+,
 *   legacy RNFS write on older versions), which writes the base64 itself.
 * iOS: writes the base64 to a temp file, then `handOffNativeFile` (the share
 *   sheet, so the user can choose "Save to Files").
 *
 * Both are given a sanitised name; see `safeFileName` above for why.
 */
const downloadAttachmentNative = async (
  base64Data: string,
  rawFileName: string,
  mimeType: string,
): Promise<void> => {
  const fileName = safeFileName(rawFileName);
  if (Platform.OS === 'android') {
    await saveToAndroidDownloads(base64Data, fileName, mimeType);
    return;
  }

  if (Platform.OS === 'ios') {
    const tempFilePath = `${RNFS.TemporaryDirectoryPath}/${fileName}`;
    await RNFS.writeFile(tempFilePath, base64Data, 'base64');
    await handOffNativeFile(tempFilePath, fileName, mimeType);
    return;
  }

  throw new Error('Unsupported platform for native download');
};

/** blob-util's time-based progress throttle for `downloadToDevice` (P4). */
export const PROGRESS_INTERVAL_MS = 250;

/** A download in flight, streamed to disk (D11). */
export interface DeviceDownload {
  promise: Promise<void>;
  /** Call it off: the transfer stops and the partial file is unlinked. */
  abort: () => void;
}

/** What blob-util's `text()` yields on a non-2xx: JSON with `error` if the
 * server had a reason, anything at all if a gateway answered. */
const reasonFromBody = (body: string | null, status: number): string => {
  if (body !== null) {
    try {
      const parsed = JSON.parse(body) as { error?: unknown };
      if (typeof parsed?.error === 'string' && parsed.error)
        return parsed.error;
    } catch {
      // Not JSON.
    }
  }
  return `Request failed (${status})`;
};

/**
 * Stream a GET straight to the device, never through JavaScript (#1069 Story
 * 7.5, D11, C9, AC 20). Native only: web has `getBlobWithProgress` +
 * `saveBlobFile`, and `useSaveAllFiles` reads `Platform.OS` in its body to
 * choose (7.2 D9).
 *
 * `react-native-blob-util`'s `config({ path })` writes the response body to
 * that path as it arrives (`fileCache` is ignored when `path` is set), so a
 * 200 MB zip costs 200 MB of disk and none of memory. On success the path is
 * handed to `handOffNativeFile`, which owns the temp file from then on
 * (Android's copy unlinks it; iOS unlinks it after the share). On a non-2xx,
 * a failure or an abort, the partial file is unlinked here.
 *
 * ⚠️ `onProgress` GETS RAW BYTES. blob-util's `total` is -1 on a chunked
 * response, the same reason the web transport hands over `loaded` (C10):
 * the caller owns the denominator.
 */
export const downloadToDevice = (args: {
  url: string;
  headers: Record<string, string>;
  fileName: string;
  mimeType: string;
  onProgress?: (receivedBytes: number) => void;
}): DeviceDownload => {
  const { url, headers, fileName, mimeType, onProgress } = args;
  const safeName = safeFileName(fileName);
  const path = `${RNBlobUtil.fs.dirs.CacheDir}/${safeName}`;
  let cancelled = false;
  /**
   * Set once the body has arrived and the hand-off has begun. From then on
   * `abort` is a no-op (review patch P5): a cancel under an open share sheet
   * or a MediaStore copy in progress would unlink the file out from under
   * them, and there is no transfer left to call off.
   */
  let landed = false;

  const unlink = () =>
    RNBlobUtil.fs.unlink(path).catch((e: unknown) => {
      console.warn('Failed to clean up temp file after download:', e);
    });

  const task = RNBlobUtil.config({ path }).fetch('GET', url, headers);
  // ⚠️ `interval`, NOT `count` (review patch P4). A streamed zip has no
  // Content-Length, so blob-util's `count` throttle divides by a
  // contentLength of -1: Android reports EVERY read (thousands of
  // `setPercent`s) and iOS reports the first 20 and then freezes. The
  // time-based throttle does not look at the total at all.
  task.progress({ interval: PROGRESS_INTERVAL_MS }, (received: number) => {
    onProgress?.(received);
  });

  const promise = (async () => {
    let response: Awaited<typeof task>;
    try {
      response = await task;
    } catch (e: unknown) {
      // ⚠️ ONE UNLINK, AFTER THE TRANSFER HAS SETTLED (review patch P5).
      // `abort` does not unlink: `task.cancel()` tears the connection down
      // asynchronously, and the writer can flush one more chunk between an
      // early unlink and the rejection, leaving a partial file behind.
      await unlink();
      if (cancelled) throw new DownloadError(0, 'Download cancelled.', true);
      throw new DownloadError(
        0,
        e instanceof Error && e.message
          ? e.message
          : 'A network error stopped the download.',
      );
    }
    if (cancelled) {
      await unlink();
      throw new DownloadError(0, 'Download cancelled.', true);
    }
    const status = response.info().status;
    if (status < 200 || status >= 300) {
      let body: string | null = null;
      try {
        body = await response.text();
      } catch {
        body = null;
      }
      await unlink();
      throw new DownloadError(status, reasonFromBody(body, status));
    }
    landed = true;
    await handOffNativeFile(path, safeName, mimeType);
  })();

  return {
    promise,
    abort: () => {
      if (landed || cancelled) return;
      cancelled = true;
      task.cancel();
    },
  };
};

/**
 * Preview/view an attachment (opens in new tab on web, uses file viewer on native)
 */
export const viewAttachment = async (attachmentId: number): Promise<void> => {
  try {
    const result = await getAttachment(attachmentId);
    if (!result.success || !result.attachment?.attachmentFile) {
      throw new Error('Failed to get attachment data');
    }

    const mimeType = result.attachment.mimeType || 'application/octet-stream';
    const dataUrl = getViewableFileUrl(
      result.attachment.attachmentFile,
      mimeType,
    );

    if (Platform.OS === 'web') {
      // Open in new tab for web
      window.open(dataUrl, '_blank');
    } else {
      // For native, try to download first, then view
      // This provides a fallback if file viewer libraries aren't available
      await downloadAttachment(attachmentId);
      console.log('File downloaded. You can find it in your Downloads folder.');
    }
  } catch (error) {
    console.error('View attachment error:', error);
    throw error;
  }
};

/**
 * Update attachment caption
 */
export const updateAttachmentCaption = async (
  attachmentId: number,
  caption: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    await api.put(`/attachments/${attachmentId}/caption`, { caption });
    return { success: true };
  } catch (error) {
    console.error('Error updating attachment caption:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const attachmentService = {
  saveAttachment,
  getAttachmentsByRequest,
  getAttachment,
  getAttachmentMetadata,
  getAttachmentThumbnail,
  updateAttachment,
  deleteAttachment,
  getViewableFileUrl,
  viewAttachment,
  downloadAttachment,
  updateAttachmentCaption,
  clearThumbnailCache,
  clearAttachmentCache,
  clearAllAttachmentCaches,
  resolveAttachmentSource,
  isInlineAttachment,
  isPdfEmbeddableImageAttachment,
};
